import { Flexbox, Form, LoadManager } from '@/components'
import useAxiosRequest from '@/hooks/useAxiosRequest'
import useMistreatment from '@/hooks/useMistreatment'
import dateMask from '@/lib/masks/date'
import yup from '@/lib/yupPt'
import { setMistreatment } from '@/reducers/mistreatment'
import mistreatments from '@/services/api/mistreatments'
import React, { useCallback, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const schema = yup.object().shape({
  eventDescription: yup.string().required().trim(),
  osmObservacao: yup.string().required().trim().min(10),
})
function OpenOsm() {
  const [creating, setCreating] = useState(false)
  const { state: { mistreatment }, dispatch } = useMistreatment()
  const history = useHistory()
  const { id } = useParams()
  const { data, loading, error } = useAxiosRequest(mistreatments.osmPreview, id)

  const { preview, assunto } = data ?? {}

  const onSubmit = useCallback(async (body) => {
    try {
      setCreating(true)
      const res = await mistreatments.osmCreate(id, body)
      dispatch(setMistreatment(res?.data?.mistreatment))
      history.goBack()
      toast.success('OS-m criada com sucesso')
    } catch (e) {
      toast.error(e?.message ?? 'Falha ao criar OS-m')
    } finally {
      setCreating(false)
    }
  }, [dispatch, history, id])

  return (
    <fieldset disabled={loading || creating}>
      <div className="modal active" id="modal-id">
        <a type="button" className="modal-overlay" aria-label="Close" onClick={() => history.goBack()} />
        <div className="modal-container">
          <div className="modal-header">
            <a
              type="button"
              className="btn btn-clear float-right"
              onClick={() => history.goBack()}
            />
            <div className="modal-title h5">Abrir OS-m para a denúncia #{id}</div>
          </div>
          <div className="modal-body">
            <div className="content">
              <LoadManager loading={loading} error={error}>
                {
                  Boolean(data) && (
                    <>
                      <div className="mb-1">Confirme os detalhes da OS-m antes de abri-lá</div>
                      <Form.Formik
                        initialValues={{
                          osmObservacao: mistreatment?.description,
                          eventDescription: '',
                        }}
                        validationSchema={schema}
                        onSubmit={onSubmit}
                        validateOnMount
                      >
                        {
                          ({ touched, errors, isValid }) => (
                            <Form.ValidationForm>
                              <Flexbox.Columns>
                                <Flexbox.Column className="col-12">
                                  <div className="divider" />
                                </Flexbox.Column>
                                <Flexbox.Column className="col-12">
                                  <div className="bg-gray s-rounded p-1">
                                    <div>Assunto da OS-m</div>
                                    <div><b>{assunto?.descricao}</b></div>
                                  </div>
                                </Flexbox.Column>
                                <Flexbox.Column className="col-4 col-sm-12">
                                  <Form.Group>
                                    <Form.Label>Protocolo número</Form.Label>
                                    <output className="form-input disabled">{preview?.protocolo_numero}</output>
                                  </Form.Group>
                                </Flexbox.Column>
                                <Flexbox.Column className="col-8 col-sm-12">
                                  <Form.Group>
                                    <Form.Label>Protocolo data</Form.Label>
                                    <output className="form-input disabled">{dateMask(preview?.protocolo_data)}</output>
                                  </Form.Group>
                                </Flexbox.Column>
                                {
                                  preview?.anexos?.length > 0 && (
                                    <Flexbox.Column className="col-12 mt-2">
                                      <Flexbox.Columns>
                                        {
                                          preview.anexos.map((anexo) => (
                                            <Flexbox.Column key={anexo.url} className="col-2">
                                              <a href={anexo.url} target="_blank" rel="noreferrer">
                                                <div className="bg-gray s-rounded p-1">
                                                  <img src={anexo.url} alt={anexo.descricao} className="s-rounded img-responsive" />
                                                  <div>
                                                    <small>
                                                      {anexo.descricao}
                                                    </small>
                                                  </div>
                                                </div>
                                              </a>
                                            </Flexbox.Column>
                                          ))
                                        }
                                      </Flexbox.Columns>
                                    </Flexbox.Column>
                                  )
                                }
                                <Flexbox.Column className="col-12">
                                  <div className="divider" />
                                </Flexbox.Column>
                                <Flexbox.Column className="col-12">
                                  <Form.Group className={`${touched?.eventDescription && errors?.eventDescription ? 'has-error' : ''}`}>
                                    <Form.Label htmlFor="eventDescription">Observações para a denúncia <span className="text-error">*</span></Form.Label>
                                    <Form.ValidationField
                                      name="eventDescription"
                                      id="eventDescription"
                                    >
                                      {
                                        ({ field }) => (
                                          <textarea
                                            name="eventDescription"
                                            className="form-input"
                                            rows={2}
                                            placeholder="Texto do evento de denúncia"
                                            // eslint-disable-next-line react/jsx-props-no-spreading
                                            {...field}
                                          />
                                        )
                                      }
                                    </Form.ValidationField>
                                    <Form.Hint>
                                      O texto informado aqui será enviado ao autor da denúncia pelo
                                      aplicativo Petis
                                    </Form.Hint>
                                    {
                                      touched?.eventDescription && errors?.eventDescription
                                        ? <Form.Hint className="text-error">{errors.eventDescription}</Form.Hint> : null
                                    }
                                  </Form.Group>
                                </Flexbox.Column>
                                <Flexbox.Column className="col-12">
                                  <Form.Group className={`${touched?.osmObservacao && errors?.osmObservacao ? 'has-error' : ''}`}>
                                    <Form.Label htmlFor="osmObservacao">Observações para a OS-m <span className="text-error">*</span></Form.Label>
                                    <Form.ValidationField
                                      name="osmObservacao"
                                      id="osmObservacao"
                                    >
                                      {
                                        ({ field }) => (
                                          <textarea
                                            name="osmObservacao"
                                            className="form-input"
                                            rows={2}
                                            placeholder="Observação da OS-m"
                                            // eslint-disable-next-line react/jsx-props-no-spreading
                                            {...field}
                                          />
                                        )
                                      }
                                    </Form.ValidationField>
                                    <Form.Hint>
                                      O texto informado aqui será adicionado no campo
                                      observações da OS-m
                                    </Form.Hint>
                                    {
                                      touched?.osmObservacao && errors?.osmObservacao
                                        ? <Form.Hint className="text-error">{errors.osmObservacao}</Form.Hint> : null
                                    }
                                  </Form.Group>
                                </Flexbox.Column>
                              </Flexbox.Columns>
                              <div className="modal-footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
                                <a
                                  className="btn mr-2"
                                  disabled={loading}
                                  onClick={() => history.goBack()}
                                >
                                  <i className="fas fa-chevron-left" />
                                  &nbsp;Voltar
                                </a>
                                <button
                                  className={`btn btn-primary ${creating ? 'loading' : ''}`}
                                  disabled={loading || !isValid}
                                  type="submit"
                                >
                                  <i className="fas fa-paper-plane" />
                                  &nbsp;Abrir OS-m
                                </button>
                              </div>
                            </Form.ValidationForm>
                          )
                        }
                      </Form.Formik>
                    </>
                  )
                }
              </LoadManager>
            </div>
          </div>
        </div>
      </div>
    </fieldset>
  )
}

export default OpenOsm
